/**
 * ADIOSOFT CENTRAL ENTITLEMENT & QUOTA SERVICE
 * 
 * Single source of truth for all quota calculations across the platform:
 * - Dashboard Overview
 * - Site Creation Wizard
 * - AI Site Generator
 * - Billing & Subscriptions
 * - Super Admin Inspection
 * - Backend / Database Validation
 * 
 * Strict Data Principles:
 * 1. Database value = System value (999 is 999, "999" is 999, 2 is 2, 1 is 1, 0 is 0).
 * 2. Never converts 999 to 2 or 1.
 * 3. Never hardcodes fallback limits (no limit = 2 or limit = 1).
 * 4. Normalizes strings and numbers safely via normalizeQuota().
 * 5. Deduplicates subscriptions by ID to prevent double-counting.
 * 6. Keeps Corporate and Yacht sectors completely isolated.
 */

import {
  IndustryType,
  SectorQuotas,
  SectorQuotaInfo,
  SiteData,
  UserSubscriptionRecord,
  UserTrialInfo,
} from '../types';
import { SUBSCRIPTION_PLANS } from '../config/constants';
import { normalizeQuota } from './quotaNormalization';

export interface QuotaDebugItem {
  subscriptionId: string;
  planId: string;
  planName: string;
  sector: IndustryType;
  status: string;
  rawWebsiteLimit: unknown;
  normalizedLimit: number;
  valid: boolean;
  error?: string;
  expireDate?: number;
  source: 'user_subscription' | 'site_subscription';
}

export interface EntitlementDebugReport {
  tenantId: string;
  isSuperAdmin: boolean;
  evaluatedAt: number;
  corporate: {
    total: number;
    used: number;
    remaining: number;
    allowed: boolean;
    status: string;
    subscriptions: QuotaDebugItem[];
  };
  yacht: {
    total: number;
    used: number;
    remaining: number;
    allowed: boolean;
    status: string;
    subscriptions: QuotaDebugItem[];
  };
}

export class QuotaService {
  /**
   * Normalizes industry / sector of a site.
   */
  public static normalizeSiteSector(site: SiteData): IndustryType {
    if (site.industry === 'YACHT_RENTAL') return 'YACHT_RENTAL';
    if (site.industry === 'CORPORATE') return 'CORPORATE';
    if (
      site.subscription?.planId?.includes('YACHT') ||
      site.subscription?.planName?.toLowerCase().includes('yat')
    ) {
      return 'YACHT_RENTAL';
    }
    return 'CORPORATE';
  }

  /**
   * Evaluates if a subscription is currently active and not expired.
   */
  public static isSubscriptionActive(
    status: string,
    expireDate?: number,
    now: number = Date.now()
  ): boolean {
    if (status !== 'ACTIVE') return false;
    if (expireDate && expireDate <= now) return false;
    return true;
  }

  /**
   * Computes sector quotas from raw user subscriptions and existing database sites.
   * Pure functional calculation guaranteeing identical results across frontend and backend.
   */
  public static calculateSectorQuotas(params: {
    userId: string;
    isSuperAdmin: boolean;
    userSubscriptions: UserSubscriptionRecord[];
    existingSites: SiteData[];
    currentTime?: number;
    userTrial?: UserTrialInfo | null;
  }): { quotas: SectorQuotas; debugReport: EntitlementDebugReport } {
    const { userId, isSuperAdmin, userSubscriptions, existingSites, currentTime, userTrial } = params;
    const now = currentTime ?? Date.now();

    // 1. Real database website count (excluding soft-deleted)
    const activeSites = (existingSites || []).filter((s) => !s.isDeleted && s.status !== 'DELETED');
    const corpUsed = activeSites.filter(
      (s) => QuotaService.normalizeSiteSector(s) === 'CORPORATE'
    ).length;
    const yachtUsed = activeSites.filter(
      (s) => QuotaService.normalizeSiteSector(s) === 'YACHT_RENTAL'
    ).length;

    // 2. SuperAdmin Entitlement: Real 999 quota, calculated used & remaining
    if (isSuperAdmin) {
      const corpRemaining = Math.max(0, 999 - corpUsed);
      const yachtRemaining = Math.max(0, 999 - yachtUsed);

      const quotas: SectorQuotas = {
        CORPORATE: {
          sector: 'CORPORATE',
          title: 'Kurumsal Web Sitesi',
          allowed: true,
          total: 999,
          used: corpUsed,
          remaining: corpRemaining,
          status: corpRemaining > 0 ? 'ACTIVE' : 'FULL',
          planName: 'Platform Super Admin',
          planPriceTRY: 0,
          rawLimit: 999,
          features: [
            'Süper Yönetici Lisansı: 999 Web Sitesi Hakkı',
            'AI Web Sitesi Generator & Görsel Editör',
            'Sınırsız Sayfa & Menü Yönetimi',
            'Tüm Sektör & Yönetim Yetkileri',
          ],
        },
        YACHT_RENTAL: {
          sector: 'YACHT_RENTAL',
          title: 'Lüks Yat Kiralama',
          allowed: true,
          total: 999,
          used: yachtUsed,
          remaining: yachtRemaining,
          status: yachtRemaining > 0 ? 'ACTIVE' : 'FULL',
          planName: 'Platform Super Admin',
          planPriceTRY: 0,
          rawLimit: 999,
          features: [
            'Süper Yönetici Lisansı: 999 Yat Sitesi Hakkı',
            'Gelişmiş Yat Filosu & Rota Yönetimi',
            'Online Rezervasyon Talep Motoru',
            'Müsaitlik ve Doluluk Takvimi',
          ],
        },
      };

      const debugReport: EntitlementDebugReport = {
        tenantId: userId,
        isSuperAdmin: true,
        evaluatedAt: now,
        corporate: {
          total: 999,
          used: corpUsed,
          remaining: corpRemaining,
          allowed: true,
          status: 'ACTIVE',
          subscriptions: [],
        },
        yacht: {
          total: 999,
          used: yachtUsed,
          remaining: yachtRemaining,
          allowed: true,
          status: 'ACTIVE',
          subscriptions: [],
        },
      };

      return { quotas, debugReport };
    }

    // 3. Process and deduplicate active user subscriptions
    // Requirement 35 & 41: Deduplicate by subscription ID and payment ID to prevent double-counting.
    const seenSubscriptionIds = new Set<string>();
    const seenPaymentIds = new Set<string>();
    const corpDebugItems: QuotaDebugItem[] = [];
    const yachtDebugItems: QuotaDebugItem[] = [];

    let corpTotal = 0;
    let yachtTotal = 0;
    let corpPlanName: string | undefined;
    let yachtPlanName: string | undefined;
    let corpPlanPrice = 15000;
    let yachtPlanPrice = 36000;
    let corpPlanId: string | undefined;
    let yachtPlanId: string | undefined;
    let corpExpireDate: number | undefined;
    let yachtExpireDate: number | undefined;

    // Track expired subscriptions for historical preservation (Requirement 20 & 21)
    let hasExpiredCorp = false;
    let expiredCorpTotal = 0;
    let expiredCorpExpireDate: number | undefined;
    let expiredCorpPlanName: string | undefined;

    let hasExpiredYacht = false;
    let expiredYachtTotal = 0;
    let expiredYachtExpireDate: number | undefined;
    let expiredYachtPlanName: string | undefined;

    // Helper to process a single subscription record
    const processSub = (
      sub: {
        id?: string;
        planId?: string;
        planName?: string;
        sector?: IndustryType;
        status: string;
        websiteLimit?: unknown;
        expireDate?: number;
        priceTRY?: number;
        lastPaymentId?: string;
        shopierOrderId?: string;
      },
      source: 'user_subscription' | 'site_subscription'
    ) => {
      const subId = sub.id || `${sub.planId}_${sub.sector}_${sub.expireDate}`;
      const paymentId = sub.lastPaymentId || sub.shopierOrderId;

      if (seenSubscriptionIds.has(subId)) {
        return; // Skip duplicate ID
      }
      if (paymentId && seenPaymentIds.has(paymentId)) {
        return; // Skip duplicate payment webhook
      }

      seenSubscriptionIds.add(subId);
      if (paymentId) seenPaymentIds.add(paymentId);

      // Determine sector
      const sector: IndustryType =
        sub.sector ||
        (sub.planId?.includes('YACHT') || sub.planName?.toLowerCase().includes('yat')
          ? 'YACHT_RENTAL'
          : 'CORPORATE');

      // Determine raw limit from DB record or linked plan definition
      let rawLimit = sub.websiteLimit;
      if (rawLimit === undefined || rawLimit === null || rawLimit === '') {
        const planConfig = sub.planId ? SUBSCRIPTION_PLANS[sub.planId as keyof typeof SUBSCRIPTION_PLANS] : undefined;
        rawLimit = planConfig?.websiteLimit;
      }

      // Strict normalization
      const norm = normalizeQuota(rawLimit);
      const isExpired = sub.expireDate ? sub.expireDate <= now : false;
      const isActive = sub.status === 'ACTIVE' && !isExpired;

      const debugItem: QuotaDebugItem = {
        subscriptionId: subId,
        planId: sub.planId || 'UNKNOWN',
        planName: sub.planName || 'Standart Plan',
        sector,
        status: isExpired ? 'EXPIRED' : sub.status,
        rawWebsiteLimit: rawLimit,
        normalizedLimit: norm.valid ? norm.value : 0,
        valid: norm.valid,
        error: norm.error,
        expireDate: sub.expireDate,
        source,
      };

      if (sector === 'CORPORATE') {
        corpDebugItems.push(debugItem);
        if (norm.valid) {
          if (isActive) {
            corpTotal += norm.value;
            corpPlanName = sub.planName || corpPlanName;
            corpPlanId = sub.planId || corpPlanId;
            if (sub.priceTRY) corpPlanPrice = sub.priceTRY;
            if (sub.expireDate) {
              corpExpireDate = Math.max(corpExpireDate || 0, sub.expireDate);
            }
          } else if (isExpired || sub.status === 'EXPIRED') {
            hasExpiredCorp = true;
            expiredCorpTotal = norm.value;
            expiredCorpExpireDate = sub.expireDate;
            expiredCorpPlanName = sub.planName;
          }
        }
      } else {
        yachtDebugItems.push(debugItem);
        if (norm.valid) {
          if (isActive) {
            yachtTotal += norm.value;
            yachtPlanName = sub.planName || yachtPlanName;
            yachtPlanId = sub.planId || yachtPlanId;
            if (sub.priceTRY) yachtPlanPrice = sub.priceTRY;
            if (sub.expireDate) {
              yachtExpireDate = Math.max(yachtExpireDate || 0, sub.expireDate);
            }
          } else if (isExpired || sub.status === 'EXPIRED') {
            hasExpiredYacht = true;
            expiredYachtTotal = norm.value;
            expiredYachtExpireDate = sub.expireDate;
            expiredYachtPlanName = sub.planName;
          }
        }
      }
    };

    // 1. Process all user subscription records (Primary Source of Truth)
    for (const sub of userSubscriptions || []) {
      processSub(sub, 'user_subscription');
    }

    // 2. Also inspect active subscriptions attached directly to non-deleted sites (if not already counted)
    for (const site of activeSites) {
      if (site.subscription) {
        processSub(
          {
            id: site.subscription.lastPaymentId || site.subscription.shopierOrderId || `site_sub_${site.id}`,
            planId: site.subscription.planId,
            planName: site.subscription.planName,
            sector: QuotaService.normalizeSiteSector(site),
            status: site.subscription.status,
            websiteLimit: site.subscription.websiteLimit,
            expireDate: site.subscription.expireDate,
            priceTRY: site.subscription.priceTRY,
            lastPaymentId: site.subscription.lastPaymentId,
            shopierOrderId: site.subscription.shopierOrderId,
          },
          'site_subscription'
        );
      }
    }

    // 4. Calculate isolated remaining quota & status (Requirements 9, 20, 21, 29, 30, 46)
    // Requirement 15 & 18: Account level MAX 1 ACTIVE WEBSITE SUBSCRIPTION
    // Corporate ACTIVE and Yacht ACTIVE cannot coexist. Only the primary active subscription is permitted.
    let singleLicenseConflictSector: IndustryType | null = null;
    if (corpTotal > 0 && yachtTotal > 0) {
      if ((corpExpireDate || 0) >= (yachtExpireDate || 0)) {
        singleLicenseConflictSector = 'YACHT_RENTAL';
      } else {
        singleLicenseConflictSector = 'CORPORATE';
      }
    }

    const hasPaidCorp = corpTotal > 0 && singleLicenseConflictSector !== 'CORPORATE';
    const hasPaidYacht = yachtTotal > 0 && singleLicenseConflictSector !== 'YACHT_RENTAL';

    // Trial evaluation:
    // Trial duration: exactly 3 days (72 hours). Total website limit: SADECE 1 ADET across the whole account.
    // Rule 13: Trial hakkı ile paid hakkı ASLA BİRLEŞTİRİLMEZ.
    // Rule 15: Corporate package (2 site) satın alınırsa toplam 2 sitedir (2 + 1 = 3 ASLA OLMAZ).
    const isTrialActive = Boolean(
      userTrial &&
      userTrial.trialStatus === 'ACTIVE' &&
      now < userTrial.trialEndDate
    );
    const isTrialExpired = Boolean(
      userTrial &&
      (userTrial.trialStatus === 'EXPIRED' || now >= userTrial.trialEndDate)
    );

    const trialDaysLeft = userTrial
      ? Math.max(0, Math.ceil((userTrial.trialEndDate - now) / (24 * 60 * 60 * 1000)))
      : 0;
    const trialHoursLeft = userTrial
      ? Math.max(0, Math.ceil((userTrial.trialEndDate - now) / (60 * 60 * 1000)))
      : 0;
    const trialExpireDate = userTrial?.trialEndDate;

    const totalSitesCreated = corpUsed + yachtUsed;

    let corpAllowed = false;
    let corpStatus: SectorQuotaInfo['status'] = 'NO_PLAN';
    let effectiveCorpTotal = 0;
    let corpRemaining = 0;
    let corpIsTrial = false;
    let corpEntitlementError: string | undefined;

    if (hasPaidCorp) {
      corpAllowed = true;
      effectiveCorpTotal = corpTotal;
      corpRemaining = Math.max(0, corpTotal - corpUsed);
      corpStatus = corpRemaining > 0 ? 'ACTIVE' : 'FULL';
      corpIsTrial = false;
    } else if (hasExpiredCorp) {
      corpAllowed = false;
      effectiveCorpTotal = expiredCorpTotal;
      corpRemaining = 0;
      corpStatus = 'EXPIRED';
      corpPlanName = expiredCorpPlanName || corpPlanName;
      corpExpireDate = expiredCorpExpireDate || corpExpireDate;
      corpIsTrial = false;
    } else if (isTrialActive && !hasPaidYacht) {
      // User is in 3-Day Trial (1 website total limit)
      if (totalSitesCreated === 0) {
        corpAllowed = true;
        effectiveCorpTotal = 1;
        corpRemaining = 1;
        corpStatus = 'ACTIVE';
        corpIsTrial = true;
        corpPlanName = '3 Günlük Ücretsiz Deneme';
        corpExpireDate = trialExpireDate;
      } else if (corpUsed >= 1) {
        // Trial was used to create a Corporate site
        corpAllowed = false;
        effectiveCorpTotal = 1;
        corpRemaining = 0;
        corpStatus = 'FULL';
        corpIsTrial = true;
        corpPlanName = '3 Günlük Ücretsiz Deneme';
        corpExpireDate = trialExpireDate;
      } else {
        // Trial was used to create a Yacht site (Rule 9 & 88)
        corpAllowed = false;
        effectiveCorpTotal = 0;
        corpRemaining = 0;
        corpStatus = 'NO_PLAN';
        corpIsTrial = false;
        corpPlanName = 'Paketsiz';
        corpEntitlementError = '3 günlük deneme hakkınızı Yat Kiralama Web Sitesi için kullandınız. Kurumsal site oluşturmak için Kurumsal paket gereklidir.';
      }
    } else if (isTrialExpired && !hasPaidYacht && corpUsed >= 1) {
      corpAllowed = false;
      effectiveCorpTotal = 1;
      corpRemaining = 0;
      corpStatus = 'EXPIRED';
      corpIsTrial = true;
      corpPlanName = '3 Günlük Deneme (Süresi Bitti)';
      corpExpireDate = trialExpireDate;
      corpEntitlementError = '3 günlük deneme süreniz sona erdi. Web sitenizi tekrar yayına almak için lütfen Kurumsal Paket satın alın.';
    } else {
      corpAllowed = false;
      effectiveCorpTotal = 0;
      corpRemaining = 0;
      corpStatus = 'NO_PLAN';
      corpPlanName = 'Paketsiz';
      corpIsTrial = false;
      if (hasPaidYacht) {
        corpEntitlementError = 'Yat Kiralama paketiniz bulunmaktadır. Kurumsal web sitesi oluşturmak için Kurumsal paket gereklidir.';
      }
    }

    let yachtAllowed = false;
    let yachtStatus: SectorQuotaInfo['status'] = 'NO_PLAN';
    let effectiveYachtTotal = 0;
    let yachtRemaining = 0;
    let yachtIsTrial = false;
    let yachtEntitlementError: string | undefined;

    if (hasPaidYacht) {
      yachtAllowed = true;
      effectiveYachtTotal = yachtTotal;
      yachtRemaining = Math.max(0, yachtTotal - yachtUsed);
      yachtStatus = yachtRemaining > 0 ? 'ACTIVE' : 'FULL';
      yachtIsTrial = false;
    } else if (hasExpiredYacht) {
      yachtAllowed = false;
      effectiveYachtTotal = expiredYachtTotal;
      yachtRemaining = 0;
      yachtStatus = 'EXPIRED';
      yachtPlanName = expiredYachtPlanName || yachtPlanName;
      yachtExpireDate = expiredYachtExpireDate || yachtExpireDate;
      yachtIsTrial = false;
    } else if (isTrialActive && !hasPaidCorp) {
      // User is in 3-Day Trial (1 website total limit)
      if (totalSitesCreated === 0) {
        yachtAllowed = true;
        effectiveYachtTotal = 1;
        yachtRemaining = 1;
        yachtStatus = 'ACTIVE';
        yachtIsTrial = true;
        yachtPlanName = '3 Günlük Ücretsiz Deneme';
        yachtExpireDate = trialExpireDate;
      } else if (yachtUsed >= 1) {
        // Trial was used to create a Yacht site
        yachtAllowed = false;
        effectiveYachtTotal = 1;
        yachtRemaining = 0;
        yachtStatus = 'FULL';
        yachtIsTrial = true;
        yachtPlanName = '3 Günlük Ücretsiz Deneme';
        yachtExpireDate = trialExpireDate;
      } else {
        // Trial was used to create a Corporate site (Rule 9 & 89)
        yachtAllowed = false;
        effectiveYachtTotal = 0;
        yachtRemaining = 0;
        yachtStatus = 'NO_PLAN';
        yachtIsTrial = false;
        yachtPlanName = 'Paketsiz';
        yachtEntitlementError = '3 günlük deneme hakkınızı Kurumsal Web Sitesi için kullandınız. Yat Kiralama sitesi oluşturmak için Yat Kiralama paketi gereklidir.';
      }
    } else if (isTrialExpired && !hasPaidCorp && yachtUsed >= 1) {
      yachtAllowed = false;
      effectiveYachtTotal = 1;
      yachtRemaining = 0;
      yachtStatus = 'EXPIRED';
      yachtIsTrial = true;
      yachtPlanName = '3 Günlük Deneme (Süresi Bitti)';
      yachtExpireDate = trialExpireDate;
      yachtEntitlementError = '3 günlük deneme süreniz sona erdi. Web sitenizi tekrar yayına almak için lütfen Yat Kiralama Paketi satın alın.';
    } else {
      yachtAllowed = false;
      effectiveYachtTotal = 0;
      yachtRemaining = 0;
      yachtStatus = 'NO_PLAN';
      yachtPlanName = 'Paketsiz';
      yachtIsTrial = false;
      if (hasPaidCorp) {
        yachtEntitlementError = 'Kurumsal paketiniz bulunmaktadır. Yat Kiralama web sitesi oluşturmak için Yat Kiralama paketi gereklidir.';
      }
    }

    const quotas: SectorQuotas = {
      CORPORATE: {
        sector: 'CORPORATE',
        title: 'Kurumsal Web Sitesi',
        allowed: corpAllowed,
        total: effectiveCorpTotal,
        used: corpUsed,
        remaining: corpRemaining,
        status: corpStatus,
        isTrial: corpIsTrial,
        trialDaysLeft: corpIsTrial ? trialDaysLeft : undefined,
        trialHoursLeft: corpIsTrial ? trialHoursLeft : undefined,
        trialExpireDate: corpIsTrial ? trialExpireDate : undefined,
        trialStatus: corpIsTrial ? (isTrialExpired ? 'EXPIRED' : 'ACTIVE') : undefined,
        trialSectorChosen: corpIsTrial && totalSitesCreated > 0 ? (corpUsed > 0 ? 'CORPORATE' : 'YACHT_RENTAL') : undefined,
        planName: corpPlanName || (corpAllowed ? 'Kurumsal Web Sitesi Paketi' : 'Paketsiz'),
        planPriceTRY: corpPlanPrice,
        planId: corpPlanId || (corpIsTrial ? 'TRIAL_3_DAY' : 'PLAN_CORP_ANNUAL'),
        expireDate: corpExpireDate,
        rawLimit: corpDebugItems[0]?.rawWebsiteLimit,
        entitlementError: corpEntitlementError,
        features: corpIsTrial
          ? corpStatus === 'EXPIRED'
            ? [
                '3 günlük deneme süresi sona erdi',
                'Mevcut siteniz ve içerikleriniz koruma altındadır',
                'Yayına devam etmek için Kurumsal Paket satın alın (15.000 TL / yıl)',
              ]
            : [
                `3 Günlük Deneme: 1 Web Sitesi Hakkı (Kalan: ${trialDaysLeft} gün)`,
                'AI Web Sitesi Generator & Görsel Editör',
                'Kurumsal Sayfa, İçerik ve Menü Düzenleme',
                'Kurumsal Blog & SEO Altyapısı',
              ]
          : corpAllowed
          ? [
              `Website Hakkı: ${effectiveCorpTotal} Web Sitesi`,
              'AI Web Sitesi Generator & Görsel Editör',
              'Sınırsız Sayfa & Menü Yönetimi',
              'Kurumsal Blog & SEO Altyapısı',
              'Özel Alan Adı Bağlama & SSL',
            ]
          : corpStatus === 'EXPIRED'
          ? [
              'Abonelik süreniz doldu',
              'Mevcut siteleriniz korunmaktadır',
              'Yeniden site oluşturmak için lütfen paketi yenileyin',
            ]
          : [
              'Website oluşturmak için bir paket satın alın',
              'AI Web Sitesi Generator & Görsel Editör',
              'Özel Alan Adı Bağlama & SSL',
            ],
      },
      YACHT_RENTAL: {
        sector: 'YACHT_RENTAL',
        title: 'Lüks Yat Kiralama',
        allowed: yachtAllowed,
        total: effectiveYachtTotal,
        used: yachtUsed,
        remaining: yachtRemaining,
        status: yachtStatus,
        isTrial: yachtIsTrial,
        trialDaysLeft: yachtIsTrial ? trialDaysLeft : undefined,
        trialHoursLeft: yachtIsTrial ? trialHoursLeft : undefined,
        trialExpireDate: yachtIsTrial ? trialExpireDate : undefined,
        trialStatus: yachtIsTrial ? (isTrialExpired ? 'EXPIRED' : 'ACTIVE') : undefined,
        trialSectorChosen: yachtIsTrial && totalSitesCreated > 0 ? (yachtUsed > 0 ? 'YACHT_RENTAL' : 'CORPORATE') : undefined,
        planName: yachtPlanName || (yachtAllowed ? 'Lüks Yat Kiralama Paketi' : 'Paketsiz'),
        planPriceTRY: yachtPlanPrice,
        planId: yachtPlanId || (yachtIsTrial ? 'TRIAL_3_DAY' : 'PLAN_YACHT_ANNUAL'),
        expireDate: yachtExpireDate,
        rawLimit: yachtDebugItems[0]?.rawWebsiteLimit,
        entitlementError: yachtEntitlementError,
        features: yachtIsTrial
          ? yachtStatus === 'EXPIRED'
            ? [
                '3 günlük deneme süresi sona erdi',
                'Mevcut yat siteniz ve yat filonuz koruma altındadır',
                'Yayına devam etmek için Yat Kiralama Paketi satın alın (36.000 TL / yıl)',
              ]
            : [
                `3 Günlük Deneme: 1 Web Sitesi Hakkı (Kalan: ${trialDaysLeft} gün)`,
                'Gelişmiş Yat Filosu & Rota Yönetimi',
                'Online Rezervasyon Talep Motoru',
                'Müsaitlik ve Doluluk Takvimi',
              ]
          : yachtAllowed
          ? [
              `Website Hakkı: ${effectiveYachtTotal} Web Sitesi`,
              'Gelişmiş Yat Filosu & Rota Yönetimi',
              'Müsaitlik ve Doluluk Takvimi',
              'Online Rezervasyon Talep Motoru',
              'Teknik Özellikler ve VIP Galeri',
            ]
          : yachtStatus === 'EXPIRED'
          ? [
              'Abonelik süreniz doldu',
              'Mevcut yat siteleriniz korunmaktadır',
              'Yeniden site oluşturmak için lütfen paketi yenileyin',
            ]
          : [
              'Website oluşturmak için bir paket satın alın',
              'Gelişmiş Yat Filosu & Rota Yönetimi',
              'Online Rezervasyon Talep Motoru',
            ],
      },
    };

    const debugReport: EntitlementDebugReport = {
      tenantId: userId,
      isSuperAdmin: false,
      evaluatedAt: now,
      corporate: {
        total: effectiveCorpTotal,
        used: corpUsed,
        remaining: corpRemaining,
        allowed: corpAllowed,
        status: corpStatus,
        subscriptions: corpDebugItems,
      },
      yacht: {
        total: effectiveYachtTotal,
        used: yachtUsed,
        remaining: yachtRemaining,
        allowed: yachtAllowed,
        status: yachtStatus,
        subscriptions: yachtDebugItems,
      },
    };

    return { quotas, debugReport };
  }

  /**
   * Central validation method checking whether a user can create a website for a sector.
   * Requirement 25 & 45.
   */
  public static canCreateWebsite(
    quotas: SectorQuotas,
    sector: IndustryType,
    isSuperAdmin: boolean = false
  ): {
    allowed: boolean;
    reason?: string;
    quota: SectorQuotaInfo;
  } {
    if (isSuperAdmin) {
      return {
        allowed: true,
        quota: quotas[sector],
      };
    }

    const quota = quotas[sector];
    if (quota.status === 'NO_PLAN' || !quota.allowed || quota.total === 0) {
      return {
        allowed: false,
        reason:
          quota.entitlementError ||
          `${quota.title} için aktif bir web sitesi paketiniz veya deneme hakkınız bulunmuyor. Yeni bir site oluşturmak için lütfen paket satın alın.`,
        quota,
      };
    }

    if (quota.status === 'EXPIRED') {
      const isTrial = quota.isTrial;
      return {
        allowed: false,
        reason:
          quota.entitlementError ||
          (isTrial
            ? `3 günlük ücretsiz deneme süreniz dolmuştur. Web sitenizi yayınlamak veya yeni site oluşturmak için lütfen ${quota.title} paketini satın alın.`
            : `${quota.title} abonelik süreniz dolmuştur. Yeni bir web sitesi oluşturmak için lütfen aboneliğinizi yenileyin.`),
        quota,
      };
    }

    if (quota.remaining <= 0 || quota.used >= quota.total) {
      const isTrial = quota.isTrial;
      return {
        allowed: false,
        reason: isTrial
          ? `3 günlük deneme süresindeki 1 web sitesi hakkınızı kullandınız (${quota.used} / ${quota.total}). Yeni bir site oluşturmak için lütfen sektör paketi edinin.`
          : `${quota.title} paketinizdeki website hakkınız doldu (${quota.used} / ${quota.total}). Yeni bir site oluşturmak için mevcut sitelerinizden birini silebilir veya ek paket edinebilirsiniz.`,
        quota,
      };
    }

    return {
      allowed: true,
      quota,
    };
  }
}
